import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Search,
  Filter,
  Grid3x3,
  List,
  Rocket,
  ExternalLink,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  Settings,
  Play,
  Globe,
  ArrowLeft,
  Check,
} from 'lucide-react';
import { apiClient } from '@/services/api';
import { Application, Template } from '@/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';

export default function Applications() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createStep, setCreateStep] = useState<'template' | 'details'>('template');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [newApp, setNewApp] = useState({ name: '', description: '' });

  // Fetch applications
  const { data, isLoading, error } = useQuery({
    queryKey: ['applications', { search: searchQuery, status: statusFilter }],
    queryFn: () => apiClient.getApplications({
      page: 1,
      limit: 20,
      status: statusFilter === 'all' ? undefined : statusFilter,
    }),
  });

  // Fetch templates for selection
  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => apiClient.getTemplates({ limit: 50 }),
    enabled: isCreateDialogOpen,
  });

  // Create application mutation
  const createMutation = useMutation({
    mutationFn: (data: { name: string; description: string; templateId: string }) =>
      apiClient.createApplication(data),
    onSuccess: (application) => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      resetCreateDialog();
      toast.success('Application created successfully');
      navigate(`/builder/${application.id}`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create application');
    },
  });

  // Delete application mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteApplication(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      toast.success('Application deleted');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete application');
    },
  });

  const filteredApplications = data?.applications?.filter(app =>
    app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.description?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'deployed': case 'DEPLOYED':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'building': case 'BUILDING':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'error': case 'FAILED':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  function resetCreateDialog() {
    setIsCreateDialogOpen(false);
    setCreateStep('template');
    setSelectedTemplate(null);
    setNewApp({ name: '', description: '' });
  }

  function handleSelectTemplate(template: Template) {
    setSelectedTemplate(template);
    setCreateStep('details');
  }

  function handleCreateApplication() {
    if (!selectedTemplate || !newApp.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    createMutation.mutate({
      name: newApp.name,
      description: newApp.description,
      templateId: selectedTemplate.id,
    });
  }

  const templates = templatesData?.templates || [];

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Applications</h1>
          <p className="text-muted-foreground mt-1">
            Build and manage your SaaS applications
          </p>
        </div>

        <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
          if (!open) resetCreateDialog();
          else setIsCreateDialogOpen(true);
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              New Application
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
            {createStep === 'template' ? (
              <>
                <DialogHeader>
                  <DialogTitle>Choose a Template</DialogTitle>
                  <DialogDescription>
                    Select a starting template for your application. You can customize it later with AI.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  {templatesLoading ? (
                    <div className="grid grid-cols-2 gap-3">
                      {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} className="h-32 w-full rounded-lg" />
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {templates.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => handleSelectTemplate(template)}
                          className="text-left p-4 rounded-lg border-2 border-muted hover:border-primary/50 hover:bg-accent/50 transition-all"
                        >
                          <div className="flex items-start justify-between">
                            <h3 className="font-semibold text-sm">{template.name}</h3>
                            {(template as any).featured && (
                              <Badge variant="secondary" className="text-xs">Featured</Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {template.description}
                          </p>
                          <div className="flex gap-1 mt-2 flex-wrap">
                            <Badge variant="outline" className="text-xs">{template.category}</Badge>
                            <Badge variant="outline" className="text-xs">{template.framework}</Badge>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>Application Details</DialogTitle>
                  <DialogDescription>
                    Using template: <strong>{selectedTemplate?.name}</strong>
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">Application Name *</Label>
                    <Input
                      id="name"
                      placeholder="My Awesome SaaS"
                      value={newApp.name}
                      onChange={(e) => setNewApp({ ...newApp, name: e.target.value })}
                      autoFocus
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Describe your application..."
                      rows={3}
                      value={newApp.description}
                      onChange={(e) => setNewApp({ ...newApp, description: e.target.value })}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateStep('template')}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={handleCreateApplication}
                    disabled={createMutation.isPending || !newApp.name}
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create Application'}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters and Search */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Search applications..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon">
              <Filter className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {['all', 'deployed', 'building', 'draft', 'error'].map((s) => (
              <DropdownMenuItem key={s} onClick={() => setStatusFilter(s)}>
                {statusFilter === s && <Check className="w-4 h-4 mr-2" />}
                {s === 'all' ? 'All Applications' : s.charAt(0).toUpperCase() + s.slice(1)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="flex gap-1 border rounded-md p-1">
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="icon"
            onClick={() => setViewMode('grid')}
          >
            <Grid3x3 className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="icon"
            onClick={() => setViewMode('list')}
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Applications Grid/List */}
      {isLoading ? (
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-2/3" />
                <Skeleton className="h-4 w-full mt-2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground">Failed to load applications</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => queryClient.invalidateQueries({ queryKey: ['applications'] })}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : filteredApplications.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Rocket className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No applications yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first application to get started
            </p>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Application
            </Button>
          </CardContent>
        </Card>
      ) : (
        <AnimatePresence mode="popLayout">
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
            {filteredApplications.map((app) => (
              <motion.div
                key={app.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
              >
                <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1" onClick={() => navigate(`/builder/${app.id}`)}>
                        <CardTitle className="text-xl">{app.name}</CardTitle>
                        <CardDescription className="mt-1 line-clamp-2">
                          {app.description}
                        </CardDescription>
                      </div>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/builder/${app.id}`)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/builder/${app.id}/settings`)}>
                            <Settings className="w-4 h-4 mr-2" />
                            Settings
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => deleteMutation.mutate(app.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <div className="flex gap-2 mt-3">
                      <Badge variant="outline" className={getStatusColor(app.status)}>
                        {app.status}
                      </Badge>
                      {app.metadata?.framework && (
                        <Badge variant="secondary">
                          {app.metadata.framework}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent onClick={() => navigate(`/builder/${app.id}`)}>
                    {app.liveUrl && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Globe className="w-4 h-4" />
                        <a
                          href={app.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-primary hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {app.liveUrl.replace(/^https?:\/\//, '')}
                          <ExternalLink className="w-3 h-3 inline ml-1" />
                        </a>
                      </div>
                    )}
                  </CardContent>

                  <CardFooter className="gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/builder/${app.id}`);
                      }}
                    >
                      Open Builder
                    </Button>
                    {(app.status === 'deployed' || (app.status as string) === 'DEPLOYED') && app.liveUrl && (
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(app.liveUrl, '_blank');
                        }}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        View Live
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </div>
        </AnimatePresence>
      )}
    </div>
  );
}
